import { PlusIcon } from "@phosphor-icons/react";
import type { SectionKey } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import get from "lodash.get";
import { useEffect, useState } from "react";

import {
  isAdditionalBuilderStep,
  isCustomBuilderStep,
  STEP_LABELS,
} from "@/client/constants/builder-steps";
import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";

import { CUSTOM_SECTION_DEFINITION, SECTION_DEFINITIONS } from "./config";
import { AdditionalItemForm } from "./item-form";
import { AdditionalItemList } from "./item-list";

type Props = { id: SectionKey };
type View = "form" | "list";

export const AdditionalSectionPage = ({ id }: Props) => {
  const { toast } = useToast();
  const section = useResumeStore((state) => get(state.resume.data.sections, id)) as
    | { name: string; items: Array<Record<string, any>> }
    | undefined;
  const setValue = useResumeStore((state) => state.setValue);
  const definition = isAdditionalBuilderStep(id)
    ? SECTION_DEFINITIONS[id]
    : CUSTOM_SECTION_DEFINITION;
  const items = section?.items ?? [];
  const [view, setView] = useState<View>(() => (items.length ? "list" : "form"));
  const [editing, setEditing] = useState<Record<string, any> | undefined>();

  useEffect(() => {
    setEditing(undefined);
    setView(items.length ? "list" : "form");
  }, [id]);

  if (!section) return null;

  const save = (item: Record<string, any>) => {
    const nextItems = editing
      ? items.map((current) => (current.id === editing.id ? item : current))
      : [...items, item];
    setValue(`sections.${id}.items`, nextItems);
    setEditing(undefined);
    setView("list");
    toast({
      variant: "success",
      title: editing ? "Élément mis à jour" : "Élément ajouté",
      description: section.name,
    });
  };

  const remove = (itemId: string) => {
    setValue(
      `sections.${id}.items`,
      items.filter((item) => item.id !== itemId),
    );
    toast({
      variant: "success",
      title: "Élément supprimé",
      description: section.name,
    });
  };

  const openCreate = () => {
    setEditing(undefined);
    setView("form");
  };

  return (
    <section className="mx-auto w-full max-w-[720px] pb-3">
      <header>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
          {isCustomBuilderStep(id) ? section.name : STEP_LABELS[id]}
        </p>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-[#071451] sm:text-[28px]">
          {definition.title}
        </h1>
        <p className="mt-1.5 max-w-xl text-xs leading-5 text-slate-500 sm:text-sm">
          {definition.subtitle}
        </p>
      </header>

      <div className="mt-5 flex border-b border-slate-200" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "form"}
          onClick={openCreate}
          className={cn(
            "inline-flex min-h-10 items-center gap-2 border-b-2 px-3 text-[11px] font-bold",
            view === "form"
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-slate-500",
          )}
        >
          <PlusIcon size={14} weight="bold" />{" "}
          {editing ? "Modifier" : `Nouveau ${definition.singular}`}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          disabled={!items.length}
          onClick={() => setView("list")}
          className={cn(
            "min-h-10 border-b-2 px-3 text-[11px] font-bold",
            view === "list"
              ? "border-indigo-600 text-indigo-700"
              : "border-transparent text-slate-500",
            !items.length && "cursor-not-allowed opacity-50",
          )}
        >
          Éléments ajoutés{" "}
          <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px]">
            {items.length}
          </span>
        </button>
      </div>

      <div className="mt-4">
        {view === "form" ? (
          <AdditionalItemForm
            definition={definition}
            item={editing}
            onCancel={() => setView(items.length ? "list" : "form")}
            onSave={save}
          />
        ) : (
          <AdditionalItemList
            definition={definition}
            items={items}
            onAdd={openCreate}
            onEdit={(item) => {
              setEditing(item);
              setView("form");
            }}
            onDelete={remove}
          />
        )}
      </div>
    </section>
  );
};
